import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);

    // Load Cal.id embed script once
    if (!window.Cal) {
      (function (C, A, L) {
        let p = function (a, ar) { a.q.push(ar); };
        let d = C.document;
        C.Cal = C.Cal || function () {
          let cal = C.Cal; let ar = arguments;
          if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; }
          if (ar[0] === L) { const api = function () { p(api, arguments); }; const namespace = ar[1]; api.q = api.q || []; if (typeof namespace === "string") { cal.ns[namespace] = cal.ns[namespace] || api; p(cal.ns[namespace], ar); p(cal, ["initNamespace", namespace]); } else p(cal, ar); return; }
          p(cal, ar);
        };
      })(window, "https://cal.id/embed-link/embed.js", "init");

      window.Cal("init", "default", { origin: "https://cal.id" });
      window.Cal.ns["default"]("ui", {
        theme: "dark",
        cssVarsPerTheme: { light: { "cal-brand": "#4b00a0" }, dark: { "cal-brand": "#ffb857" } },
        hideEventTypeDetails: false,
        layout: "month_view"
      });
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-[rgba(26,28,30,0.8)] backdrop-blur-lg border-b border-[rgba(255,255,255,0.1)]' : 'bg-transparent'
    }`}>
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => scrollToSection('hero')}
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-200 flex-shrink-0"
          >
            <img src="/eshwald-icon.jpg" alt="Eshwald Ops Icon" className="h-10 w-10 rounded-lg object-cover" />
            <span className="text-2xl font-bold tracking-tight">
              <span className="text-white">Eshwald</span>
              <span className="text-[rgb(197,155,47)] ml-1">Ops</span>
            </span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-5 lg:gap-8">
            <button onClick={() => scrollToSection('hero')} className="text-[rgb(218,218,218)] hover:text-[rgb(197,155,47)] transition-colors duration-200 text-[15px] font-medium whitespace-nowrap">Home</button>
            <button onClick={() => scrollToSection('why-choose-us')} className="text-[rgb(218,218,218)] hover:text-[rgb(197,155,47)] transition-colors duration-200 text-[15px] font-medium whitespace-nowrap">Why Choose Us</button>
            <button onClick={() => scrollToSection('about')} className="text-[rgb(218,218,218)] hover:text-[rgb(197,155,47)] transition-colors duration-200 text-[15px] font-medium whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('faq')} className="text-[rgb(218,218,218)] hover:text-[rgb(197,155,47)] transition-colors duration-200 text-[15px] font-medium whitespace-nowrap">FAQ</button>

            {/* Book a Call — purple-outlined, same size as Get Started */}
            <button
              data-cal-link="eshwald-ops/discovery"
              data-cal-namespace="default"
              data-cal-config='{"layout":"month_view"}'
              className="bg-transparent border-2 border-[rgb(124,56,178)] text-white px-5 py-2 rounded-lg font-semibold text-[15px] hover:border-[rgb(156,80,210)] hover:bg-[rgba(124,56,178,0.1)] transition-all duration-200 hover:-translate-y-[1px] whitespace-nowrap"
            >
              Book a Call
            </button>

            <button
              onClick={() => scrollToSection('contact')}
              className="bg-[rgb(197,155,47)] text-[rgb(17,17,19)] px-5 py-2 rounded-lg font-semibold text-[15px] hover:bg-[rgb(224,194,95)] transition-all duration-200 hover:-translate-y-[1px] whitespace-nowrap"
            >
              Get Started
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white hover:text-[rgb(197,155,47)] transition-colors duration-200"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[rgb(26,28,30)] border-t border-[rgba(255,255,255,0.1)]">
          <div className="px-6 py-4 space-y-3">
            <button onClick={() => scrollToSection('hero')} className="block w-full text-left text-[rgb(218,218,218)] hover:text-[rgb(197,155,47)] transition-colors duration-200 py-2 text-[15px] font-medium">Home</button>
            <button onClick={() => scrollToSection('why-choose-us')} className="block w-full text-left text-[rgb(218,218,218)] hover:text-[rgb(197,155,47)] transition-colors duration-200 py-2 text-[15px] font-medium">Why Choose Us</button>
            <button onClick={() => scrollToSection('about')} className="block w-full text-left text-[rgb(218,218,218)] hover:text-[rgb(197,155,47)] transition-colors duration-200 py-2 text-[15px] font-medium">About</button>
            <button onClick={() => scrollToSection('faq')} className="block w-full text-left text-[rgb(218,218,218)] hover:text-[rgb(197,155,47)] transition-colors duration-200 py-2 text-[15px] font-medium">FAQ</button>
            <button
              data-cal-link="eshwald-ops/discovery"
              data-cal-namespace="default"
              data-cal-config='{"layout":"month_view"}'
              className="w-full bg-transparent border-2 border-[rgb(124,56,178)] text-white px-5 py-2.5 rounded-lg font-semibold text-[15px] hover:bg-[rgba(124,56,178,0.1)] transition-colors duration-200 mt-2"
            >
              Book a Call
            </button>
            <button onClick={() => scrollToSection('contact')} className="w-full bg-[rgb(197,155,47)] text-[rgb(17,17,19)] px-6 py-2.5 rounded-lg font-semibold text-[15px] hover:bg-[rgb(224,194,95)] transition-colors duration-200">Get Started</button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;